import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '@/styles/css/colors';
import type { SimpleBarChartProps } from './types';

/**
 * Minimal dependency-free bar chart rendered with flex/div bars.
 * Good enough for CloudWatch-style daily counts without pulling in a charting lib.
 * Uses named export to match codebase conventions.
 */
export const SimpleBarChart: React.FC<SimpleBarChartProps> = ({ title, labels, values, height = 400 }) => {
  // Memoize maximum value calculation to prevent recalculation on every render
  const max = useMemo(() => Math.max(...values, 1), [values]);

  // Early return for empty data state
  if (!labels.length) {
    return (
      <Box role="region" aria-label={`${title} - No data`}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box role="region" aria-label={title}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }} component="h3">
        {title}
      </Typography>
      <Box
        role="img"
        aria-label={`Bar chart showing ${labels.length} data points`}
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '8px',
          height,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          px: 2,
          pt: 2,
          overflowX: 'auto',
          position: 'relative',
        }}
      >
        {labels.map((label, index) => {
          const value = values[index] ?? 0;
          const barHeight = Math.max((value / max) * (height - 80), 2);
          return (
            <Box
              key={`${label}-${index}`}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}
            >
              <Typography 
                variant="caption" 
                sx={{ fontSize: '11px', mb: 0.5, fontWeight: 500 }}
                aria-label={`Value: ${value}`}
              >
                {value}
              </Typography>
              <Box
                aria-hidden="true"
                sx={{
                  width: 28,
                  height: barHeight,
                  backgroundColor: colors.DASHBOARDGRAPH,
                  borderRadius: '4px 4px 0 0',
                }}
              />
              <Typography
                variant="caption"
                sx={{ 
                  fontSize: '10px', 
                  mt: 1, 
                  whiteSpace: 'nowrap', 
                  transform: 'rotate(-45deg)', 
                  transformOrigin: 'top center',
                  lineHeight: 1.2,
                }}
                aria-label={`Label: ${label}`}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
